import {
  composeClassNames,
  ifDefined,
  universalEach,
} from "@faedev/editor-utils";
import Link from "next/link";
import { CSSProperties } from "react";
import { Button, ListGroup, OverlayTrigger, Tooltip } from "react-bootstrap";
import { ReactNode } from "react-markdown/lib/react-markdown";
import Heading from "src/components/Heading";
import Paragraph from "src/components/Paragraph";
import Translate from "src/components/Translate";
import constants from "src/constants";
import gutter from "src/functions/gutter";
import validateUrl from "src/functions/validateUrl";
import { sAttachedDocuments } from "src/styles/sAttachedDocuments.module.scss";
import { sButton } from "src/styles/sButton.module.scss";
import { sListGroupTable } from "src/styles/sListGroupTable.module.scss";
import { ApiDocument } from "src/types/ApiDocument";

type Props = {
  style?: CSSProperties | null | undefined;
  className?: (string | string) | null | undefined;
  data: ApiDocument[];
  parentPaid: boolean;
  isPaid: boolean;
};

function AttachedDocuments({
  style,
  className,
  data,
  parentPaid,
  isPaid,
}: Props) {
  const renderTooltip = (props) => (
    <Tooltip id="button-tooltip" {...props}>
      <Translate tKey={`The document can be downloaded after payment.`} />
    </Tooltip>
  );
  const downloadButton = (item: ApiDocument): ReactNode => {
    const url = item && item?.file && typeof item?.file === "string";
    return !parentPaid ||
      (parentPaid && !item.paid) ||
      (parentPaid && item.paid && isPaid) ? (
      <Link
        href={validateUrl(
          !url && typeof item?.file !== "string"
            ? item.file?.url
            : typeof item?.file === "object"
            ? item.file.url
            : item?.file //undefined
        )}
        passHref
        key={`document-${item.id}`}
      >
        <Button
          as="a"
          variant="outline-primary"
          size="sm"
          className={composeClassNames([sButton, `btn-download`])}
        >
          {!url &&
            typeof item.file !== "string" &&
            item.file?.mime.replace("application/", "").toUpperCase()}
          {!url && typeof item.file !== "string" && (
            <>
              &nbsp;<small>`(${item.file?.size} kB)`</small>
            </>
          )}
          {url && <Translate tKey="Download" />}
        </Button>
      </Link>
    ) : (
      <OverlayTrigger
        placement="top"
        delay={constants.Intervals.overlayDelay}
        overlay={renderTooltip}
        rootClose
      >
        <div className={"btn-disabled-overlay"}>
          <Button
            variant="outline-primary"
            size="sm"
            className={composeClassNames([sButton, `btn-download`])}
            disabled
          >
            {!url &&
              typeof item.file !== "string" &&
              item.file?.mime.replace("application/", "").toUpperCase()}
            &nbsp;
            <small>
              ({!url && typeof item.file !== "string" && item.file?.size} kB)
            </small>
          </Button>
        </div>
      </OverlayTrigger>
    );
  };
  return constants.Show.contentDocuments && data && data.length > 0 ? (
    data[0] && data[0].file !== undefined ? (
      <div
        className={composeClassNames([sAttachedDocuments, className])}
        style={style}
      >
        <div
          className={composeClassNames([sListGroupTable, "list-group-table"])}
        >
          <ListGroup horizontal={true} className={`list-header`}>
            <ListGroup.Item variant="primary">
              <Translate tKey={`Document`} />
            </ListGroup.Item>
            <ListGroup.Item variant="primary" className={`mq-gte-tablet`}>
              <Translate tKey={`Download`} />
            </ListGroup.Item>
          </ListGroup>
          {universalEach(data, (item, i) =>
            ifDefined(
              item.file,
              () => {
                return (
                  <ListGroup horizontal={true} key={i} className={`list-item`}>
                    <ListGroup.Item>
                      <Heading
                        type="h4"
                        title={item.title ? item.title : item.name}
                        style={{ margin: 0 }}
                      />
                      {item.description ?? (
                        <Paragraph>{item.description}</Paragraph>
                      )}
                      <div
                        className={`mq-phone`}
                        style={gutter(`margin`, `vertical`, `md`)}
                      >
                        {downloadButton(item)}
                      </div>
                    </ListGroup.Item>
                    <ListGroup.Item className={`mq-gte-tablet`}>
                      {downloadButton(item)}
                    </ListGroup.Item>
                  </ListGroup>
                );
              },
              () => null
            )
          )}
        </div>
      </div>
    ) : null
  ) : null;
}

export default AttachedDocuments;
